import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Mail, Send, X, CheckCircle, AlertCircle } from 'lucide-react';
import { emailAPI } from '../../services/api';
import toast from 'react-hot-toast';

/**
 * Reusable email modal for sending invoices, bills, payslips, etc.
 *
 * Props:
 *   show        — boolean to control visibility
 *   onClose     — callback when modal is closed
 *   type        — 'invoice' | 'bill' | 'payslip' | 'check' | 'estimate' | 'sales_order' | 'purchase_order' | 'generic'
 *   refId       — the document ID (invoice id, bill id, etc.)
 *   number      — document number for display (e.g. "INV-001")
 *   recipientEmail — pre-filled recipient email
 *   recipientName  — pre-filled recipient name (for subject)
 *   subject     — optional default subject line
 *   body        — optional default body text
 *   company     — company data object (name, email, etc.)
 */
export default function EmailModal({
  show,
  onClose,
  type = 'generic',
  refId,
  number,
  recipientEmail = '',
  recipientName = '',
  subject = '',
  body = '',
}) {
  const [to, setTo] = useState(recipientEmail);
  const [cc, setCc] = useState('');
  const [emailSubject, setEmailSubject] = useState(subject);
  const [emailBody, setEmailBody] = useState(body);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  // Auto-generate defaults when modal opens
  useEffect(() => {
    if (!show) return;
    setTo(recipientEmail);
    setCc('');
    setSent(false);
    setError('');

    // Auto-generate subject
    if (subject) {
      setEmailSubject(subject);
    } else {
      const typeLabels = {
        invoice: 'Invoice',
        bill: 'Bill',
        payslip: 'Pay Slip',
        check: 'Check',
        estimate: 'Estimate',
        sales_order: 'Sales Order',
        purchase_order: 'Purchase Order',
        generic: 'Document',
      };
      const label = typeLabels[type] || 'Document';
      setEmailSubject(`${label}${number ? ' #' + number : ''} from LedgerPro`);
    }

    // Auto-generate body
    if (body) {
      setEmailBody(body);
    } else {
      const namePart = recipientName ? `Dear ${recipientName},\n\n` : '';
      const typeMessages = {
        invoice: `${namePart}Please find attached your invoice${number ? ' #' + number : ''}. Kindly review the details and process payment at your earliest convenience.\n\nIf you have any questions, please don't hesitate to reach out.`,
        bill: `${namePart}Please find attached the bill${number ? ' #' + number : ''} for your review and approval.\n\nThank you.`,
        payslip: `${namePart}Please find attached your pay slip${number ? ' for the period ending ' + new Date().toLocaleDateString() : ''}.\n\nIf you have any questions regarding your compensation, please reach out to the HR department.`,
        check: `${namePart}Please find attached a copy of the check${number ? ' #' + number : ''}.\n\nThank you.`,
        estimate: `${namePart}Please find attached the estimate${number ? ' #' + number : ''}. This estimate is valid for 30 days.\n\nPlease let us know if you'd like to proceed.`,
        sales_order: `${namePart}Please find attached the sales order${number ? ' #' + number : ''} for your reference.\n\nThank you for your business.`,
        purchase_order: `${namePart}Please find attached the purchase order${number ? ' #' + number : ''}.\n\nPlease confirm receipt at your earliest convenience.`,
        generic: `${namePart}Please find attached the document for your review.\n\nThank you.`,
      };
      setEmailBody(typeMessages[type] || typeMessages.generic);
    }
  }, [show, recipientEmail, recipientName, subject, body, type, number]);

  const handleSend = async () => {
    if (!to.trim()) {
      setError('Recipient email is required');
      return;
    }
    if (!emailSubject.trim()) {
      setError('Subject is required');
      return;
    }

    setSending(true);
    setError('');

    try {
      const result = await emailAPI.send({
        to: to.trim(),
        cc: cc.trim() || undefined,
        subject: emailSubject.trim(),
        body: emailBody,
        type,
        refId: refId || undefined,
      });

      setSent(true);
      toast.success(result.data?.message || 'Email sent successfully!');

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send email');
      toast.error('Failed to send email');
    }
    setSending(false);
  };

  if (!show) return null;

  const typeLabels = {
    invoice: 'Invoice',
    bill: 'Bill',
    payslip: 'Pay Slip',
    check: 'Check',
    estimate: 'Estimate',
    sales_order: 'Sales Order',
    purchase_order: 'Purchase Order',
    generic: 'Document',
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <div
        className="modal"
        style={{ maxWidth: 560, width: '100%' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Mail size={18} style={{ color: 'var(--brand-accent)' }} />
          <h3 style={{ margin: 0, flex: 1 }}>
            Send {typeLabels[type] || 'Document'}{number ? ` #${number}` : ''}
          </h3>
          <button type="button" className="btn-ghost" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="modal-body">
          {sent ? (
            <div style={{ textAlign: 'center', padding: '30px 20px' }}>
              <CheckCircle size={48} style={{ color: '#16a34a', marginBottom: 12 }} />
              <h3 style={{ margin: '0 0 8px', color: '#16a34a' }}>Email Sent!</h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                The {typeLabels[type]?.toLowerCase() || 'document'} has been emailed to {to}
              </p>
            </div>
          ) : (
            <>
              {/* Error display */}
              {error && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
                  padding: '8px 12px', marginBottom: 16, fontSize: 12, color: '#991b1b',
                }}>
                  <AlertCircle size={14} />
                  {error}
                </div>
              )}

              {/* SMTP not configured notice */}
              <div style={{
                background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
                padding: '8px 12px', marginBottom: 16, fontSize: 11, color: '#92400e',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span>💡</span>
                <span>
                  Configure SMTP settings in <code>.env</code> (<code>SMTP_HOST</code>, <code>SMTP_USER</code>, <code>SMTP_PASS</code>) for real email delivery. Emails will be simulated in development.
                </span>
              </div>

              {/* To */}
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  To <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={to}
                  onChange={e => setTo(e.target.value)}
                  placeholder="recipient@example.com"
                  autoFocus
                  style={{ fontSize: 13 }}
                />
              </div>

              {/* CC */}
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  CC
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={cc}
                  onChange={e => setCc(e.target.value)}
                  placeholder="cc@example.com (optional)"
                  style={{ fontSize: 13 }}
                />
              </div>

              {/* Subject */}
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  Subject <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={emailSubject}
                  onChange={e => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                  style={{ fontSize: 13 }}
                />
              </div>

              {/* Body */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  Message
                </label>
                <textarea
                  className="form-control"
                  value={emailBody}
                  onChange={e => setEmailBody(e.target.value)}
                  rows={7}
                  placeholder="Email body..."
                  style={{ fontSize: 13, lineHeight: 1.5, resize: 'vertical' }}
                />
              </div>
            </>
          )}
        </div>

        {!sent && (
          <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={sending}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSend}
              disabled={sending || !to.trim()}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {sending ? (
                <>
                  <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={14} />
                  Send Email
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
